/**
 * `notesModule: EngineNotesModule` (W1-06 engine-notes-frame; ARCHITECTURE.md §7.2, §15).
 *
 * Everything per note and per frame that the renderer and the audio scheduler need, exactly as Phira:
 *   runtime.ts / sortKeys.ts   per-line struct-of-arrays runtimes (the only note seconds), Phira in-line order
 *   multiHint.ts               global exact-f64 multi-highlight map, incremental per line
 *   culling.ts                 Phira's literal walk with aggressive culling
 *   visibility.ts              R§4.5 rules (UI mover, alpha < 0, judged, hold end, cover, fade, visibleTime)
 *   noteGeom.ts / holds.ts     placement, controls, incline, MH widths, hold slicing
 *   quads.ts                   bucketed NoteQuadBatch in Phira's draw order, explicit colour clamp
 *   badNotes.ts                judged play: fading Bad-note ghosts (trailing buckets of the note batch)
 *   ghosts.ts                  hidden-by-rule outlines with reasons
 *   emissions.ts               autoplay emissions (hits, hold starts/ticks/ends, sounds, filters)
 *   hitFx.ts / particles.ts    hit-effect sprites and seeded square particles (autoplay and judged play)
 *   holdTicks.ts               judged play: hold tick range from the frames (release seen across frames)
 *   hud.ts / judgedHud.ts      HUD values (judged play maintained incrementally) and both attach-UI layouts
 *   paint.ts                   paint stamps on a 60 Hz grid from the last clear
 *   appear.ts                  read-time query (bounded look-back window)
 * State lives in `ctx.notesStore` (per-line runtimes) and a WeakMap keyed by it (store.ts); runtimes heal
 * themselves when the IR line, tempo, maxTime or speed settings changed, so every query is consistent even when a
 * compile call was skipped.
 */

import type { Emission, EngineNotesModule } from '../types';
import { noteAppearSec } from './appear';
import { collectEmissions, emissionCache } from './emissions';
import { pushGhosts, pushNoteQuadsAndGhosts } from './ghosts';
import { pushHitFx } from './hitFx';
import { autoplayHudValues, buildHud } from './hud';
import { paintStamps } from './paint';
import { pushNoteQuads } from './quads';
import { applyMultiHint, compileLine, dropLineRt, globalOf, invalidateTempo, locateNote, realNoteIndex } from './store';
import { Search } from '../../math/search';
import type { NoteId } from '../../model/types';

export { createNoteQuadBatch } from './quads';
export { APPEAR_LOOKBACK_SEC } from './appear';
export { BAD_GHOST_KIND_ORDER, isBadGhostBucket } from './badNotes';
export type { HudTextMeasurer } from './hud';
export type { NoteEmission } from './emissions';

export const notesModule: EngineNotesModule = {
  compileLineNotes(ctx, line) {
    compileLine(ctx, line);
  },
  dropLine(ctx, line) {
    dropLineRt(ctx, line);
  },
  onTempoChanged(ctx) {
    invalidateTempo(ctx);
  },
  updateMultiHint(ctx, changedLines) {
    applyMultiHint(ctx, globalOf(ctx), changedLines);
  },
  rebuildEmissions(ctx) {
    emissionCache(ctx, true);
  },
  noteSec(ctx, note) {
    const loc = locateNote(ctx, note);
    return loc === null ? Number.NaN : (loc.rt.time[loc.i] ?? Number.NaN);
  },
  noteEndSec(ctx, note) {
    const loc = locateNote(ctx, note);
    return loc === null ? Number.NaN : (loc.rt.endTime[loc.i] ?? Number.NaN);
  },
  isMultiHighlight(ctx, note) {
    const loc = locateNote(ctx, note);
    if (loc === null) return false;
    return globalOf(ctx).mh.isMulti(loc.rt.time[loc.i] ?? Number.NaN);
  },
  noteAppearSec(ctx, note, player, skin, winW, winH, atLeast) {
    return noteAppearSec(ctx, note, player, skin, winW, winH, atLeast);
  },
  notesInWindow(ctx, t0, t1) {
    const idx = realNoteIndex(ctx);
    const a = Search.lowerBound(idx.time, t0);
    const b = Search.upperBound(idx.time, t1);
    const out: NoteId[] = [];
    for (let k = a; k < b; k++) out.push((idx.id[k] ?? 0) as NoteId);
    return out;
  },
  emissions(ctx, t0, t1, rate, filters, out: Emission[]) {
    collectEmissions(ctx, t0, t1, rate, filters, out);
  },
  pushNoteQuads(ctx, states, t, opts, out) {
    // With ghosts on, one walk serves both passes (the ghost call of the same frame then keeps its result).
    if (opts.ghosts) pushNoteQuadsAndGhosts(ctx, states, t, opts, out);
    else pushNoteQuads(ctx, states, t, opts, out);
  },
  ghosts(ctx, states, t, opts, out) {
    pushGhosts(ctx, states, t, opts, out);
  },
  hitFx(ctx, t, opts, out) {
    pushHitFx(ctx, t, opts, out);
  },
  paintStamps(ctx, line, t, winW) {
    return paintStamps(ctx, line, t, winW);
  },
  hud(ctx, states, t, opts, viewport) {
    return buildHud(ctx, states, t, opts, viewport);
  },
  hudValues(ctx, t) {
    return autoplayHudValues(ctx, t);
  },
};
